import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { MdPeople } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { api } from "../../services/api";
import { repositorioFornecedor } from "../../sync/repositories/financeiro";

const AJUDA = {
  titulo: "Cadastro de Fornecedores",
  descricao:
    "Cadastre aqui empresas ou pessoas que vendem produtos ou prestam serviços para a fazenda. Esse cadastro é usado depois nas Contas a Pagar e nas Notas Fiscais de entrada — quanto mais completo, mais fácil fica localizar e emitir pagamentos para esse fornecedor.",
  secoes: [
    {
      titulo: "Tipo de Pessoa",
      campos: [
        {
          nome: "Pessoa Jurídica (CNPJ) / Pessoa Física (CPF)",
          obrigatorio: true,
          explicacao:
            "Escolha primeiro se o fornecedor é uma empresa (Jurídica) ou uma pessoa (Física) — isso muda quais campos aparecem em 'Dados Principais'.",
        },
      ],
    },
    {
      titulo: "Dados Principais",
      campos: [
        {
          nome: "Razão Social",
          obrigatorio: true,
          explicacao: "Nome oficial da empresa, exatamente como consta no CNPJ. Só aparece para Pessoa Jurídica.",
        },
        {
          nome: "Nome Fantasia",
          obrigatorio: false,
          explicacao: "Nome popular/comercial da empresa, se for diferente da razão social.",
        },
        {
          nome: "CNPJ",
          obrigatorio: true,
          explicacao: "Documento da empresa. Só aparece para Pessoa Jurídica.",
        },
        {
          nome: "Nome Completo",
          obrigatorio: true,
          explicacao: "Nome completo da pessoa. Só aparece para Pessoa Física.",
        },
        {
          nome: "CPF",
          obrigatorio: true,
          explicacao: "Documento da pessoa. Só aparece para Pessoa Física.",
        },
        {
          nome: "Status",
          obrigatorio: false,
          explicacao:
            "Ativo (compra normalmente), Inativo, Suspenso ou Bloqueado. Fornecedores inativos continuam no histórico, mas somem das listas de seleção em novos lançamentos.",
        },
      ],
    },
    {
      titulo: "Endereço",
      campos: [
        {
          nome: "CEP",
          obrigatorio: false,
          explicacao: "Digite o CEP e clique em 'Buscar' (ou saia do campo) para preencher automaticamente logradouro, bairro, cidade e estado.",
        },
      ],
    },
    {
      titulo: "Contato",
      campos: [
        {
          nome: "Telefone / Celular / WhatsApp / E-mail",
          obrigatorio: false,
          explicacao: "Formas de contato direto com o fornecedor ou com o responsável comercial.",
        },
      ],
    },
    {
      titulo: "Comercial",
      campos: [
        {
          nome: "Prazo de Entrega / Condição e Prazo de Pagamento",
          obrigatorio: false,
          explicacao:
            "Informações de referência sobre como esse fornecedor costuma negociar — ajuda ao criar uma Conta a Pagar para ele, mas não é copiado automaticamente para lá.",
        },
      ],
    },
    {
      titulo: "Dados Bancários",
      campos: [
        {
          nome: "Banco / Agência / Conta Corrente / Chave PIX",
          obrigatorio: false,
          explicacao: "Dados para referência de pagamento (transferência ou PIX).",
        },
      ],
    },
  ],
  dicas: [
    "Excluir um fornecedor com contas ou notas fiscais vinculadas não é permitido — o sistema vai oferecer a opção de 'inativar' em vez de excluir, o que preserva o histórico.",
    "Se estiver sem internet, o cadastro/edição funciona normalmente e sincroniza depois — apenas a exclusão exige conexão, porque o sistema precisa checar no servidor se há vínculos antes de apagar.",
  ],
};

export default function GestaoFornecedores() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedoresFiltrados, setFornecedoresFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState("dados");
  const [mensagem, setMensagem] = useState("");
  const [modalInativacaoOpen, setModalInativacaoOpen] = useState(false);
  const [fornecedorBloqueado, setFornecedorBloqueado] = useState(null);
  const [inativandoFornecedor, setInativandoFornecedor] = useState(false);

  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);

  const [filtros, setFiltros] = useState({
    busca: "",
    status: "",
  });

  const [fornecedor, setFornecedor] = useState({
    tipo_pessoa: "J",
    razao_social: "",
    nome_fantasia: "",
    cnpj: "",
    insc_est: "",
    insc_municipal: "",
    cnae: "",
    nome_completo: "",
    cpf: "",
    rg: "",
    data_nascimento: "",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    pais: "Brasil",
    telefone: "",
    celular: "",
    whatsapp: "",
    email: "",
    responsavel: "",
    telefone_responsavel: "",
    prazo_entrega: "",
    condicao_pagamento: "",
    prazo_pagamento: "",
    banco: "",
    agencia: "",
    conta_corrente: "",
    pix: "",
    regime_tributario: "",
    status: "A",
    observacoes: "",
    historico_compras: "",
  });

  // Estados brasileiros
  const estados = [
    "AC",
    "AL",
    "AP",
    "AM",
    "BA",
    "CE",
    "DF",
    "ES",
    "GO",
    "MA",
    "MT",
    "MS",
    "MG",
    "PA",
    "PB",
    "PR",
    "PE",
    "PI",
    "RJ",
    "RN",
    "RS",
    "RO",
    "RR",
    "SC",
    "SP",
    "SE",
    "TO",
  ];

  // Regimes tributários
  const regimesTributarios = [
    { value: "SN", label: "Simples Nacional" },
    { value: "LP", label: "Lucro Presumido" },
    { value: "LR", label: "Lucro Real" },
    { value: "MEI", label: "MEI" },
    { value: "OUT", label: "Outro" },
  ];

  const statusList = [
    { value: "A", label: "Ativo", cor: "bg-green-100 text-green-800" },
    { value: "I", label: "Inativo", cor: "bg-red-100 text-red-800" },
    { value: "S", label: "Suspenso", cor: "bg-yellow-100 text-yellow-800" },
    { value: "B", label: "Bloqueado", cor: "bg-gray-100 text-gray-800" },
  ];

  // Carregar fornecedores
  const carregarFornecedores = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioFornecedor.listar();
      const dados = response.data.results || response.data;
      setFornecedores(Array.isArray(dados) ? dados : []);
      setFornecedoresFiltrados(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar fornecedores:", error);
      alert("Erro ao carregar lista de fornecedores");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarFornecedores();
  }, [carregarFornecedores]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFornecedor((prev) => ({ ...prev, [name]: value }));
  };

  const handleTipoPessoaChange = (tipo) => {
    setFornecedor((prev) => ({ ...prev, tipo_pessoa: tipo }));
  };

  const buscarCep = async () => {
    if (!fornecedor.cep || fornecedor.cep.length < 8) return;

    try {
      const cepLimpo = fornecedor.cep.replace(/\D/g, "");
      const response = await axios.get(
        `https://viacep.com.br/ws/${cepLimpo}/json/`,
      );

      if (!response.data.erro) {
        setFornecedor((prev) => ({
          ...prev,
          logradouro: response.data.logradouro || "",
          bairro: response.data.bairro || "",
          cidade: response.data.localidade || "",
          estado: response.data.uf || "",
        }));
        setMensagem("");
      } else {
        setMensagem("CEP não encontrado");
        setTimeout(() => setMensagem(""), 3000);
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      setMensagem("Erro ao buscar CEP");
      setTimeout(() => setMensagem(""), 3000);
    }
  };

  const validarFormulario = () => {
    if (fornecedor.tipo_pessoa === "J") {
      if (!fornecedor.razao_social.trim()) {
        alert("Preencha a razão social.");
        return false;
      }
      if (!fornecedor.cnpj) {
        alert("Preencha o CNPJ.");
        return false;
      }
    } else {
      if (!fornecedor.nome_completo.trim()) {
        alert("Preencha o nome completo.");
        return false;
      }
      if (!fornecedor.cpf) {
        alert("Preencha o CPF.");
        return false;
      }
    }
    return true;
  };

  // Função para validar e formatar data antes de enviar
  const formatarDataParaEnvio = (data) => {
    if (!data) return null;

    // Se já estiver no formato YYYY-MM-DD, retorna
    if (/^\d{4}-\d{2}-\d{2}$/.test(data)) {
      return data;
    }

    // Tenta converter de outros formatos
    try {
      // Se for DD/MM/YYYY
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(data)) {
        const partes = data.split("/");
        return `${partes[2]}-${partes[1]}-${partes[0]}`;
      }

      // Se for um objeto Date válido
      const dataObj = new Date(data);
      if (!isNaN(dataObj.getTime())) {
        const ano = dataObj.getFullYear();
        const mes = String(dataObj.getMonth() + 1).padStart(2, "0");
        const dia = String(dataObj.getDate()).padStart(2, "0");
        return `${ano}-${mes}-${dia}`;
      }

      return null;
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) return;

    setLoading(true);

    // Cria uma cópia do fornecedor e formata a data
    const payload = {
      ...fornecedor,
      data_nascimento: formatarDataParaEnvio(fornecedor.data_nascimento),
    };

    try {
      if (editando) {
        await repositorioFornecedor.atualizar(editandoId, payload);
        setMensagem("Fornecedor atualizado com sucesso!");
        handleCancel();
        carregarFornecedores();
      } else {
        await repositorioFornecedor.criar(payload);
        setMensagem("Fornecedor cadastrado com sucesso!");
        handleCancel();
        carregarFornecedores();
      }
      setTimeout(() => setMensagem(""), 3000);
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (fornecedorItem) => {
    // Formata a data de nascimento para o formato do input date (YYYY-MM-DD)
    let dataNascimentoFormatada = fornecedorItem.data_nascimento || "";

    // Se a data existir e não estiver no formato YYYY-MM-DD, converte
    if (
      dataNascimentoFormatada &&
      !/^\d{4}-\d{2}-\d{2}$/.test(dataNascimentoFormatada)
    ) {
      try {
        const dataObj = new Date(dataNascimentoFormatada);
        if (!isNaN(dataObj.getTime())) {
          const ano = dataObj.getFullYear();
          const mes = String(dataObj.getMonth() + 1).padStart(2, "0");
          const dia = String(dataObj.getDate()).padStart(2, "0");
          dataNascimentoFormatada = `${ano}-${mes}-${dia}`;
        }
      } catch {
        dataNascimentoFormatada = "";
      }
    }

    setFornecedor({
      tipo_pessoa: fornecedorItem.tipo_pessoa || "J",
      razao_social: fornecedorItem.razao_social || "",
      nome_fantasia: fornecedorItem.nome_fantasia || "",
      cnpj: fornecedorItem.cnpj || "",
      insc_est: fornecedorItem.insc_est || "",
      insc_municipal: fornecedorItem.insc_municipal || "",
      cnae: fornecedorItem.cnae || "",
      nome_completo: fornecedorItem.nome_completo || "",
      cpf: fornecedorItem.cpf || "",
      rg: fornecedorItem.rg || "",
      data_nascimento: dataNascimentoFormatada,
      cep: fornecedorItem.cep || "",
      logradouro: fornecedorItem.logradouro || "",
      numero: fornecedorItem.numero || "",
      complemento: fornecedorItem.complemento || "",
      bairro: fornecedorItem.bairro || "",
      cidade: fornecedorItem.cidade || "",
      estado: fornecedorItem.estado || "",
      pais: fornecedorItem.pais || "Brasil",
      telefone: fornecedorItem.telefone || "",
      celular: fornecedorItem.celular || "",
      whatsapp: fornecedorItem.whatsapp || "",
      email: fornecedorItem.email || "",
      responsavel: fornecedorItem.responsavel || "",
      telefone_responsavel: fornecedorItem.telefone_responsavel || "",
      prazo_entrega: fornecedorItem.prazo_entrega || "",
      condicao_pagamento: fornecedorItem.condicao_pagamento || "",
      prazo_pagamento: fornecedorItem.prazo_pagamento || "",
      banco: fornecedorItem.banco || "",
      agencia: fornecedorItem.agencia || "",
      conta_corrente: fornecedorItem.conta_corrente || "",
      pix: fornecedorItem.pix || "",
      regime_tributario: fornecedorItem.regime_tributario || "",
      status: fornecedorItem.status || "A",
      observacoes: fornecedorItem.observacoes || "",
      historico_compras: fornecedorItem.historico_compras || "",
    });
    setEditando(true);
    setEditandoId(fornecedorItem.id);
    setMostrarFormulario(true);
    setAbaAtiva("dados");

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleNovoFornecedor = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFornecedor({
      tipo_pessoa: "J",
      razao_social: "",
      nome_fantasia: "",
      cnpj: "",
      insc_est: "",
      insc_municipal: "",
      cnae: "",
      nome_completo: "",
      cpf: "",
      rg: "",
      data_nascimento: "",
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      pais: "Brasil",
      telefone: "",
      celular: "",
      whatsapp: "",
      email: "",
      responsavel: "",
      telefone_responsavel: "",
      prazo_entrega: "",
      condicao_pagamento: "",
      prazo_pagamento: "",
      banco: "",
      agencia: "",
      conta_corrente: "",
      pix: "",
      regime_tributario: "",
      status: "A",
      observacoes: "",
      historico_compras: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
    setAbaAtiva("dados");
    setMensagem("");
  };

  const handleExcluir = async (id) => {
    const confirmar = window.confirm(
      "Tem certeza que deseja excluir este fornecedor? Esta ação não pode ser desfeita.",
    );

    if (!confirmar) return;

    try {
      await api.delete(`/financeiro/fornecedores/${id}/`);

      alert("Fornecedor excluído com sucesso!");
      carregarFornecedores();

      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir fornecedor:", error);

      const data = error?.response?.data;

      // Caso o backend tenha retornado o fluxo de inativação
      if (error?.response?.status === 400 && data?.pode_inativar) {
        setFornecedorBloqueado({
          id: data.fornecedor_id,
          detail: data.detail,
          referencias: data.referencias || [],
          ativo: data.fornecedor_ativo,
        });
        setModalInativacaoOpen(true);
        return;
      }

      // Se já estiver inativo ou vier algum erro mais específico
      if (data?.detail) {
        alert(data.detail);
        return;
      }

      alert(
        "Erro ao excluir fornecedor. Verifique se há registros vinculados.",
      );
    }
  };

  const handleInativarFornecedor = async () => {
    if (!fornecedorBloqueado?.id) return;

    try {
      setInativandoFornecedor(true);

      await api.patch(
        `/financeiro/fornecedores/${fornecedorBloqueado.id}/inativar/`,
      );

      alert("Fornecedor inativado com sucesso!");
      setModalInativacaoOpen(false);
      setFornecedorBloqueado(null);

      carregarFornecedores();

      if (editandoId === fornecedorBloqueado.id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao inativar fornecedor:", error);
      const detail =
        error?.response?.data?.detail || "Erro ao inativar fornecedor.";
      alert(detail);
    } finally {
      setInativandoFornecedor(false);
    }
  };

  const fecharModalInativacao = () => {
    if (inativandoFornecedor) return;
    setModalInativacaoOpen(false);
    setFornecedorBloqueado(null);
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(fornecedores) ? [...fornecedores] : [];

    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(
        (f) =>
          f.razao_social?.toLowerCase().includes(buscaLower) ||
          f.nome_fantasia?.toLowerCase().includes(buscaLower) ||
          f.nome_completo?.toLowerCase().includes(buscaLower) ||
          f.cnpj?.includes(buscaLower) ||
          f.cpf?.includes(buscaLower) ||
          f.email?.toLowerCase().includes(buscaLower),
      );
    }

    if (filtros.status) {
      filtrados = filtrados.filter((f) => f.status === filtros.status);
    }

    setFornecedoresFiltrados(filtrados);
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", status: "" });
    setFornecedoresFiltrados(Array.isArray(fornecedores) ? fornecedores : []);
    setPaginaAtual(1);
  };

  // Paginação
  const indiceUltimoItem = paginaAtual * itensPorPagina;
  const indicePrimeiroItem = indiceUltimoItem - itensPorPagina;
  const fornecedoresPaginados = fornecedoresFiltrados.slice(
    indicePrimeiroItem,
    indiceUltimoItem,
  );
  const totalPaginas = Math.ceil(fornecedoresFiltrados.length / itensPorPagina);

  const formatarDocumento = (fornecedorItem) => {
    if (fornecedorItem.tipo_pessoa === "J") {
      return fornecedorItem.cnpj || "—";
    }
    return fornecedorItem.cpf || "—";
  };

  const getNomeFornecedor = (fornecedorItem) => {
    if (fornecedorItem.tipo_pessoa === "J") {
      return fornecedorItem.razao_social || fornecedorItem.nome_fantasia || "—";
    }
    return fornecedorItem.nome_completo || "—";
  };

  const getStatusInfo = (status) => {
    const statusInfo = statusList.find((s) => s.value === status);
    return statusInfo || { label: status, cor: "bg-gray-100 text-gray-800" };
  };

  const formatarTelefone = (value) => {
    if (!value) return "";
    const telefone = value.replace(/\D/g, "");
    if (telefone.length === 11) {
      return telefone.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    }
    if (telefone.length === 10) {
      return telefone.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    }
    return value;
  };

  const formatarCnpj = (value) => {
    if (!value) return "";
    const cnpj = value.replace(/\D/g, "");
    if (cnpj.length <= 14) {
      return cnpj.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5",
      );
    }
    return value;
  };

  const formatarCpf = (value) => {
    if (!value) return "";
    const cpf = value.replace(/\D/g, "");
    if (cpf.length <= 11) {
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return value;
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              Gestão de Fornecedores <MdPeople size={38} />
            </h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoFornecedor}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Novo Fornecedor
            </button>
          )}
        </div>

        {/* Mensagem de feedback */}
        {mensagem && (
          <div
            className={`mb-4 p-3 rounded ${mensagem.includes("sucesso") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
          >
            {mensagem}
          </div>
        )}

        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Fornecedor" : "Cadastrar Novo Fornecedor"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Tipo de Pessoa */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="J"
                      checked={fornecedor.tipo_pessoa === "J"}
                      onChange={() => handleTipoPessoaChange("J")}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="font-medium">Pessoa Jurídica (CNPJ)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="F"
                      checked={fornecedor.tipo_pessoa === "F"}
                      onChange={() => handleTipoPessoaChange("F")}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="font-medium">Pessoa Física (CPF)</span>
                  </label>
                </div>
              </div>

              {/* Abas */}
              <div className="border-b mb-6">
                <nav className="flex gap-2 flex-wrap">
                  {[
                    { id: "dados", label: "Dados Principais" },
                    { id: "endereco", label: "Endereço" },
                    { id: "contato", label: "Contato" },
                    { id: "comercial", label: "Comercial" },
                    { id: "bancario", label: "Dados Bancários" },
                    { id: "outros", label: "Outras Informações" },
                  ].map((aba) => (
                    <button
                      key={aba.id}
                      type="button"
                      onClick={() => setAbaAtiva(aba.id)}
                      className={`px-4 py-2 rounded-t-lg transition-colors ${
                        abaAtiva === aba.id
                          ? "bg-green-600 text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {aba.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Conteúdo das Abas */}
              <div className="space-y-4">
                {/* Aba Dados Principais */}
                {abaAtiva === "dados" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {fornecedor.tipo_pessoa === "J" ? (
                      <>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            Razão Social *
                          </label>
                          <input
                            type="text"
                            name="razao_social"
                            value={fornecedor.razao_social}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            required
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            Nome Fantasia
                          </label>
                          <input
                            type="text"
                            name="nome_fantasia"
                            value={fornecedor.nome_fantasia}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            CNPJ *
                          </label>
                          <input
                            type="text"
                            name="cnpj"
                            value={formatarCnpj(fornecedor.cnpj)}
                            onChange={(e) =>
                              handleChange({
                                target: {
                                  name: "cnpj",
                                  value: e.target.value.replace(/\D/g, ""),
                                },
                              })
                            }
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            maxLength="18"
                            required
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            Inscrição Estadual
                          </label>
                          <input
                            type="text"
                            name="insc_est"
                            value={fornecedor.insc_est}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            Inscrição Municipal
                          </label>
                          <input
                            type="text"
                            name="insc_municipal"
                            value={fornecedor.insc_municipal}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            CNAE
                          </label>
                          <input
                            type="text"
                            name="cnae"
                            value={fornecedor.cnae}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            placeholder="Código da atividade econômica"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-col md:col-span-2">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            Nome Completo *
                          </label>
                          <input
                            type="text"
                            name="nome_completo"
                            value={fornecedor.nome_completo}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            required
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            CPF *
                          </label>
                          <input
                            type="text"
                            name="cpf"
                            value={formatarCpf(fornecedor.cpf)}
                            onChange={(e) =>
                              handleChange({
                                target: {
                                  name: "cpf",
                                  value: e.target.value.replace(/\D/g, ""),
                                },
                              })
                            }
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            maxLength="14"
                            required
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            RG
                          </label>
                          <input
                            type="text"
                            name="rg"
                            value={fornecedor.rg}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            Data de Nascimento
                          </label>
                          <input
                            type="date"
                            name="data_nascimento"
                            value={fornecedor.data_nascimento || ""}
                            onChange={handleChange}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          />
                        </div>
                      </>
                    )}

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        name="status"
                        value={fornecedor.status}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      >
                        {statusList.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Aba Endereço */}
                {abaAtiva === "endereco" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        CEP
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name="cep"
                          value={fornecedor.cep}
                          onChange={handleChange}
                          onBlur={buscarCep}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          placeholder="00000-000"
                        />
                        <button
                          type="button"
                          onClick={buscarCep}
                          className="bg-blue-600 text-white px-3 rounded hover:bg-blue-700 transition-colors"
                        >
                          Buscar
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Logradouro
                      </label>
                      <input
                        type="text"
                        name="logradouro"
                        value={fornecedor.logradouro}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Número
                      </label>
                      <input
                        type="text"
                        name="numero"
                        value={fornecedor.numero}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Complemento
                      </label>
                      <input
                        type="text"
                        name="complemento"
                        value={fornecedor.complemento}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Bairro
                      </label>
                      <input
                        type="text"
                        name="bairro"
                        value={fornecedor.bairro}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Cidade
                      </label>
                      <input
                        type="text"
                        name="cidade"
                        value={fornecedor.cidade}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Estado
                      </label>
                      <select
                        name="estado"
                        value={fornecedor.estado}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      >
                        <option value="">Selecione</option>
                        {estados.map((uf) => (
                          <option key={uf} value={uf}>
                            {uf}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        País
                      </label>
                      <input
                        type="text"
                        name="pais"
                        value={fornecedor.pais}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                  </div>
                )}

                {/* Aba Contato */}
                {abaAtiva === "contato" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Telefone
                      </label>
                      <input
                        type="text"
                        name="telefone"
                        value={formatarTelefone(fornecedor.telefone)}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: "telefone",
                              value: e.target.value.replace(/\D/g, ""),
                            },
                          })
                        }
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Celular
                      </label>
                      <input
                        type="text"
                        name="celular"
                        value={formatarTelefone(fornecedor.celular)}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: "celular",
                              value: e.target.value.replace(/\D/g, ""),
                            },
                          })
                        }
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        WhatsApp
                      </label>
                      <input
                        type="text"
                        name="whatsapp"
                        value={formatarTelefone(fornecedor.whatsapp)}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: "whatsapp",
                              value: e.target.value.replace(/\D/g, ""),
                            },
                          })
                        }
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        E-mail
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={fornecedor.email}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Responsável/Contato
                      </label>
                      <input
                        type="text"
                        name="responsavel"
                        value={fornecedor.responsavel}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Telefone do Responsável
                      </label>
                      <input
                        type="text"
                        name="telefone_responsavel"
                        value={formatarTelefone(
                          fornecedor.telefone_responsavel,
                        )}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: "telefone_responsavel",
                              value: e.target.value.replace(/\D/g, ""),
                            },
                          })
                        }
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                  </div>
                )}

                {/* Aba Comercial */}
                {abaAtiva === "comercial" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Prazo de Entrega
                      </label>
                      <input
                        type="text"
                        name="prazo_entrega"
                        value={fornecedor.prazo_entrega}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Ex: 15 dias úteis"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Condição de Pagamento
                      </label>
                      <input
                        type="text"
                        name="condicao_pagamento"
                        value={fornecedor.condicao_pagamento}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Ex: 30/60/90 dias"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Prazo de Pagamento
                      </label>
                      <input
                        type="text"
                        name="prazo_pagamento"
                        value={fornecedor.prazo_pagamento}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Ex: 30 dias após entrega"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Regime Tributário
                      </label>
                      <select
                        name="regime_tributario"
                        value={fornecedor.regime_tributario}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      >
                        <option value="">Selecione</option>
                        {regimesTributarios.map((reg) => (
                          <option key={reg.value} value={reg.value}>
                            {reg.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Aba Dados Bancários */}
                {abaAtiva === "bancario" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Banco
                      </label>
                      <input
                        type="text"
                        name="banco"
                        value={fornecedor.banco}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Agência
                      </label>
                      <input
                        type="text"
                        name="agencia"
                        value={fornecedor.agencia}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Conta Corrente
                      </label>
                      <input
                        type="text"
                        name="conta_corrente"
                        value={fornecedor.conta_corrente}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Chave PIX
                      </label>
                      <input
                        type="text"
                        name="pix"
                        value={fornecedor.pix}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="CPF, e-mail, telefone ou chave aleatória"
                      />
                    </div>
                  </div>
                )}

                {/* Aba Outras Informações */}
                {abaAtiva === "outros" && (
                  <div className="space-y-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Observações
                      </label>
                      <textarea
                        name="observacoes"
                        value={fornecedor.observacoes}
                        onChange={handleChange}
                        rows="4"
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Observações gerais sobre o fornecedor"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Histórico de Compras
                      </label>
                      <textarea
                        name="historico_compras"
                        value={fornecedor.historico_compras}
                        onChange={handleChange}
                        rows="4"
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Registro histórico de compras realizadas"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Botões do formulário */}
              <div className="flex gap-4 pt-6 mt-4 border-t">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                >
                  {loading
                    ? "Salvando..."
                    : editando
                      ? "Atualizar"
                      : "Cadastrar"}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                Filtros de Busca
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Buscar por Nome, Documento ou E-mail
                  </label>
                  <input
                    type="text"
                    placeholder="Digite o nome, CNPJ/CPF ou e-mail..."
                    value={filtros.busca}
                    onChange={(e) =>
                      setFiltros({ ...filtros, busca: e.target.value })
                    }
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filtros.status}
                    onChange={(e) =>
                      setFiltros({ ...filtros, status: e.target.value })
                    }
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todos</option>
                    {statusList.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Fornecedores */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                <p className="text-gray-600">
                  {fornecedoresFiltrados.length} fornecedor(es) encontrado(s)
                </p>
                <div className="text-sm text-gray-500">
                  Página {paginaAtual} de {totalPaginas || 1}
                </div>
              </div>

              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">
                  Carregando...
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Código
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Tipo
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Nome / Razão Social
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Documento
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Cidade/UF
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Status
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {fornecedoresPaginados.map((fornecedorItem) => {
                          const statusInfo = getStatusInfo(
                            fornecedorItem.status,
                          );
                          return (
                            <tr
                              key={fornecedorItem.id}
                              className="border-t border-gray-200 hover:bg-gray-50 transition-colors"
                            >
                              <td className="p-3 text-sm font-mono">
                                {fornecedorItem.id}
                              </td>
                              <td className="p-3 text-sm">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${fornecedorItem.tipo_pessoa === "J" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}`}
                                >
                                  {fornecedorItem.tipo_pessoa === "J"
                                    ? "Jurídica"
                                    : "Física"}
                                </span>
                              </td>
                              <td className="p-3 text-sm font-medium">
                                <span className="flex items-center gap-1">
                                  {getNomeFornecedor(fornecedorItem)}
                                </span>
                              </td>
                              <td className="p-3 text-sm font-mono">
                                {formatarDocumento(fornecedorItem)}
                              </td>
                              <td className="p-3 text-sm">
                                {fornecedorItem.cidade || "—"}
                                {fornecedorItem.estado
                                  ? `/${fornecedorItem.estado}`
                                  : ""}
                              </td>
                              <td className="p-3">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.cor}`}
                                >
                                  {statusInfo.label}
                                </span>
                              </td>
                              <td className="p-3">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEditar(fornecedorItem)}
                                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                    title="Editar"
                                  >
                                    Editar
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleExcluir(fornecedorItem.id)
                                    }
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                    title="Excluir"
                                  >
                                    Excluir
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}

                        {fornecedoresPaginados.length === 0 && (
                          <tr>
                            <td
                              colSpan="7"
                              className="p-8 text-center text-gray-500"
                            >
                              {filtros.busca || filtros.status
                                ? "Nenhum fornecedor encontrado com os filtros selecionados."
                                : "Nenhum fornecedor cadastrado."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {totalPaginas > 1 && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                      <button
                        onClick={() =>
                          setPaginaAtual((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={paginaAtual === 1}
                        className={`px-4 py-2 rounded transition-colors ${
                          paginaAtual === 1
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                      >
                        Anterior
                      </button>
                      <div className="flex gap-1">
                        {Array.from(
                          { length: totalPaginas },
                          (_, i) => i + 1,
                        ).map((num) => (
                          <button
                            key={num}
                            onClick={() => setPaginaAtual(num)}
                            className={`w-8 h-8 rounded transition-colors ${
                              paginaAtual === num
                                ? "bg-green-600 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() =>
                          setPaginaAtual((prev) =>
                            Math.min(prev + 1, totalPaginas),
                          )
                        }
                        disabled={paginaAtual === totalPaginas}
                        className={`px-4 py-2 rounded transition-colors ${
                          paginaAtual === totalPaginas
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                      >
                        Próxima
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {modalInativacaoOpen && fornecedorBloqueado && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
              <div className="border-b border-gray-200 px-6 py-4">
                <h3 className="text-xl font-semibold text-gray-800">
                  Não foi possível excluir o fornecedor
                </h3>
              </div>

              <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
                <p className="text-sm text-gray-700">
                  {fornecedorBloqueado.detail ||
                    "Este fornecedor possui registros vinculados e não pode ser excluído."}
                </p>

                {fornecedorBloqueado.referencias?.length > 0 && (
                  <div className="mt-4">
                    <p className="mb-2 text-sm font-medium text-gray-800">
                      Registros vinculados:
                    </p>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <ul className="space-y-2 text-sm text-gray-700">
                        {fornecedorBloqueado.referencias.map((ref, index) => (
                          <li
                            key={`${ref.tipo}-${ref.id}-${index}`}
                            className="rounded-lg border border-gray-200 bg-white px-3 py-2"
                          >
                            <span className="font-semibold">{ref.tipo}</span>
                            {ref.descricao ? ` — ${ref.descricao}` : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm text-amber-900">
                    Você pode <strong>inativar</strong> este fornecedor para
                    removê-lo da listagem de ativos sem apagar o histórico já
                    vinculado.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={fecharModalInativacao}
                  disabled={inativandoFornecedor}
                  className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={handleInativarFornecedor}
                  disabled={inativandoFornecedor}
                  className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {inativandoFornecedor
                    ? "Inativando..."
                    : "Inativar fornecedor"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
